import axios, { AxiosResponse } from 'axios';

declare global {
  interface Window {
    __ENV__?: { REACT_APP_API_URL?: string };
  }
}

// public/env.js, rewritten for each environment when the site is deployed,
// carries the API address; the build-time value is only the fallback.
export const API_BASE_URL = ((typeof window !== 'undefined' ? window.__ENV__?.REACT_APP_API_URL : undefined) || process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

// The backend also serves the booking widget, so widget links use its origin.
export const API_ORIGIN = new URL(API_BASE_URL, typeof window !== 'undefined' ? window.location.origin : 'https://orarivo.com').origin;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let refreshPromise: Promise<string> | null = null;

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    const entityKeys = ['appointment', 'appointments', 'service', 'services', 'staff', 'resource', 'resources', 'tenant', 'client', 'clients', 'documents', 'document', 'enrollment'];
    const normalize = (record: any): any => {
      if (Array.isArray(record)) return record.map(normalize);
      if (!record || typeof record !== 'object') return record;
      return Object.fromEntries(Object.entries(record).map(([key, value]) => [
        key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()),
        key === 'price' && value !== null ? Number(value) : value,
      ]));
    };
    for (const key of entityKeys) {
      if (response.data?.[key]) response.data[key] = normalize(response.data[key]);
    }
    return response;
  },
  async (error) => {
    if (!error.response) error.message = 'Nu se poate contacta serverul. Verifică conexiunea și încearcă din nou.';
    if (error.response && (!error.response.data || typeof error.response.data !== 'object')) {
      error.response.data = { error: error.response.status === 429
        ? 'Au fost trimise prea multe cereri. Așteaptă puțin și încearcă din nou.'
        : 'Serverul nu este disponibil momentan. Încercați din nou.' };
    }
    if (error.response?.data) {
      const data = error.response.data;
      const validationMessages = Array.isArray(data.details)
        ? Array.from(new Set(data.details.map((item: any) => item?.msg).filter((message: any) => typeof message === 'string'))).join('. ')
        : '';
      data.error = validationMessages ||
        (typeof data.error === 'string' ? data.error : data.error?.message) || data.message || 'A apărut o eroare. Încercați din nou.';
    }
    if (error.response?.data?.code === 'FEATURE_DISABLED') window.dispatchEvent(new Event('organization-features-changed'));
    const originalRequest = error.config;
    const isAuthRequest = /\/auth\/(login|register|refresh)$/.test(originalRequest?.url || '');

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          if (!refreshPromise) {
            refreshPromise = axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
              .then(response => response.data.tokens.accessToken)
              .finally(() => { refreshPromise = null; });
          }
          const accessToken = await refreshPromise;
          localStorage.setItem('token', accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }): Promise<AxiosResponse> =>
    api.post('/auth/login', credentials),
  
  register: (data: {
    tenantName: string;
    subdomain: string;
    email: string;
    password: string;
    fullName: string;
    businessType?: string;
    phone?: string;
    address?: string;
  }): Promise<AxiosResponse> =>
    api.post('/auth/register', data),
  
  getProfile: (): Promise<AxiosResponse> =>
    api.get('/auth/profile'),
  
  updateProfile: (data: {
    fullName?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<AxiosResponse> =>
    api.put('/auth/profile', data),
  
  logout: (): Promise<AxiosResponse> =>
    api.post('/auth/logout'),
};

// Convert local form dates explicitly and allow optional fields to be cleared.
export const appointmentPayload = (data: any) => ({
  ...data,
  ...(data.startTime ? { startTime: new Date(data.startTime).toISOString() } : {}),
  ...(data.endTime ? { endTime: new Date(data.endTime).toISOString() } : {}),
  ...('clientEmail' in data ? { clientEmail: data.clientEmail || null } : {}),
  ...('resourceId' in data ? { resourceId: data.resourceId || null } : {}),
});

// Appointments API
export const appointmentsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    staffId?: string; clientId?: string; resourceId?: string; licenseCategory?: string; drivingType?: string;
    start?: string;
    end?: string;
  }): Promise<AxiosResponse> =>
    api.get('/appointments', { params }),
  
  getById: (id: string): Promise<AxiosResponse> =>
    api.get(`/appointments/${id}`),
  
  create: (data: any): Promise<AxiosResponse> =>
    api.post('/appointments', appointmentPayload(data)),
  
  update: (id: string, data: any): Promise<AxiosResponse> =>
    api.put(`/appointments/${id}`, appointmentPayload(data)),
  
  delete: (id: string): Promise<AxiosResponse> =>
    api.delete(`/appointments/${id}`),
  
  bulk: (action: string, appointmentIds: string[]): Promise<AxiosResponse> =>
    api.post('/appointments/bulk', { action, appointmentIds }),

  getAvailability: (params: {
    date: string;
    duration?: number;
  }): Promise<AxiosResponse> =>
    api.get('/appointments/public/availability', { params }),
};

// Services API
export const servicesAPI = {
  getAll: (params?: { active?: boolean; category?: string }): Promise<AxiosResponse> =>
    api.get('/services', { params }),
  
  getById: (id: string): Promise<AxiosResponse> =>
    api.get(`/services/${id}`),
  
  create: (data: any): Promise<AxiosResponse> =>
    api.post('/services', data),
  
  update: (id: string, data: any): Promise<AxiosResponse> =>
    api.put(`/services/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse> =>
    api.delete(`/services/${id}`),
  
  getCategories: (): Promise<AxiosResponse> =>
    api.get('/services/meta/categories'),
};

// Staff API
export const staffAPI = {
  getAll: (params?: { active?: boolean }): Promise<AxiosResponse> =>
    api.get('/staff', { params }),
  
  getById: (id: string): Promise<AxiosResponse> =>
    api.get(`/staff/${id}`),
  
  create: (data: any): Promise<AxiosResponse> =>
    api.post('/staff', { ...data, email: data.email || null }),
  
  update: (id: string, data: any): Promise<AxiosResponse> =>
    api.put(`/staff/${id}`, { ...data, ...('email' in data ? { email: data.email || null } : {}) }),
  
  delete: (id: string): Promise<AxiosResponse> =>
    api.delete(`/staff/${id}`),
};

// Resources API
export const resourcesAPI = {
  getAll: (params?: { active?: boolean; type?: string }): Promise<AxiosResponse> =>
    api.get('/resources', { params }),
  
  getById: (id: string): Promise<AxiosResponse> =>
    api.get(`/resources/${id}`),
  
  create: (data: any): Promise<AxiosResponse> =>
    api.post('/resources', data),
  
  update: (id: string, data: any): Promise<AxiosResponse> =>
    api.put(`/resources/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse> =>
    api.delete(`/resources/${id}`),
  
  getTypes: (): Promise<AxiosResponse> =>
    api.get('/resources/meta/types'),
};

// Tenants API
export const tenantsAPI = {
  updateLogo: (logoUrl: string | null): Promise<AxiosResponse> =>
    api.put('/tenants/logo', { logoUrl }),
  getCurrent: (): Promise<AxiosResponse> =>
    api.get('/tenants/current'),
  
  updateCurrent: (data: any): Promise<AxiosResponse> =>
    api.put('/tenants/current', data),
  
  getStats: (): Promise<AxiosResponse> =>
    api.get('/tenants/stats'),
  
  getSettings: (): Promise<AxiosResponse> =>
    api.get('/tenants/settings'),
  
  updateSettings: (settings: any): Promise<AxiosResponse> =>
    api.put('/tenants/settings', { settings }),
};

// Settings API
export const settingsAPI = {
  getAll: (): Promise<AxiosResponse> =>
    api.get('/config'),
  
  get: (key: string): Promise<AxiosResponse> =>
    api.get(`/config/${key}`),
  
  update: (key: string, data: { value: any; description?: string }): Promise<AxiosResponse> =>
    api.put(`/config/${key}`, data),
  
  delete: (key: string): Promise<AxiosResponse> =>
    api.delete(`/config/${key}`),
  
  getWorkingHoursTemplates: (): Promise<AxiosResponse> =>
    api.get('/config/templates/working-hours'),
  
  getBookingSettingsTemplates: (): Promise<AxiosResponse> =>
    api.get('/config/templates/booking-settings'),
};

export default api;
